/**
 * Design tab shell. The grouped left nav (DesignNav) lists the Build Plan
 * card, Design Docs, and Screens; the centre panel renders the editor for
 * whatever's selected; the right rail stacks the compact Build
 * card (BuildRailCard — build status + primary action) over the
 * Co-Designer. Workflow state derives from design-status.ts so every surface
 * agrees. Sections use the WYSIWYG editor (TipTap). Self-contained React
 * state — the full Anvil Zustand store port is a later phase.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type {
  BuildUpdateStatus,
  DesignChangeSummary,
  DesignProject,
  DesignReconcileStatus,
} from '@volley/crucible-ipc-types';
import type { AutoBuildStatus } from '@volley/crucible-ipc-types/build-game-types';
import { Wand2, Rows3, FileText } from 'lucide-react';
import {
  getBuildSnapshotSection,
  getBuildUpdateStatus,
  getReconcileStatus,
  openProject,
  readSection,
  setSectionItemMode,
  writeProjectProfile,
  writeSection,
} from './anvil-api';
import type { DesignProfile } from '@volley/crucible-ipc-types';
import { BuildHandoffDialog } from './BuildHandoffDialog';
import { SectionWysiwyg } from './SectionWysiwyg';
import { SectionItemsEditor } from './SectionItemsEditor';
import { ItemProposalDialog } from './ItemProposalDialog';
import {
  itemModeFor,
  mergeItemsMarkdown,
  appendFreeformItem,
  replyHasItems,
  isGameLoopSection,
} from './lib/section-items';
import { CoPilotPanel } from './CoPilotPanel';
import { ScreenDetail, useScreens } from './ScreenEditor';
import { generateScreensFromDesign } from './screen-generate';
import { SyncDesignDialog, type SyncDesignDialogMode } from './SyncDesignDialog';
import { ReconcileDialog } from './ReconcileDialog';
import { WizardDialog } from './WizardDialog';
import { LensPanel, type LensSelection } from './LensPanel';
import { DesignNav } from './DesignNav';
import { GameProfile } from './GameProfile';
import { Tip } from './Tip';
import { BuildRailCard, type DesignBuildActivity } from './BuildRailCard';
import {
  deriveBuildCardModel,
  deriveBuildPlanChip,
  deriveNavFooterLine,
  deriveScreensGenerateState,
  designChangesPending,
  type DesignStatusInputs,
} from './design-status';
import { isScreensStale, markDocsChanged, markScreensGenerated } from './screens-stale';
import { PlanWorkspace, type PlanWorkspaceProps } from '../../components/PlanWorkspace';
import type { DesignBuildPlanSeed } from '@volley/crucible-ipc-types';

/** Placeholder marker written by the scaffolder into a fresh section body. */
const PLACEHOLDER_MARKER = '_(empty — fill in';

export interface DesignWorkspaceProps {
  gameId: string;
  /** Existing build-game plan surface embedded inside Design. */
  buildPlan?: Omit<PlanWorkspaceProps, 'gameId' | 'gameName' | 'onStartBuilding'> & {
    gameName: string;
    focusNonce: number;
    onStartBuilding: (seededPlan?: DesignBuildPlanSeed) => Promise<void> | void;
  };
  /** Called when a FULL build is sent ("Send to build"), so the host can
   *  start the build through the existing build-game state machine. */
  onSentToBuild?: (seededPlan?: DesignBuildPlanSeed) => Promise<void> | void;
  /** Called when build UPDATES are accepted ("Send design changes"): the host
   *  switches to the Build surface and auto-sends the edit list to the agent. */
  onSendUpdate?: (message: string) => void;
  /** Live build progress for the right-rail Build card. */
  buildActivity?: DesignBuildActivity | null;
  /** Automatic build-first setup state, when Fast/Just Build owns drafting. */
  autoBuildStatus?: AutoBuildStatus | null;
}

/** What the centre panel is showing. */
type View =
  | { kind: 'section'; filename: string }
  | { kind: 'screen'; screenId: string | null }
  | { kind: 'build-plan' };

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type ReconcileStatusState = { gameId: string; status: DesignReconcileStatus };
type ReconcileOpenState = { gameId: string; open: boolean };
type DesignAuthorshipState = 'unknown' | 'scaffolded' | 'authored';

function isAuthoredSectionBody(text: string): boolean {
  return text.trim() !== '' && !text.includes(PLACEHOLDER_MARKER);
}

function deriveDesignAuthorship(bodies: string[]): DesignAuthorshipState {
  return bodies.some(isAuthoredSectionBody) ? 'authored' : 'scaffolded';
}

/**
 * Merges server-reported design diffs with client-local pending section edits.
 *
 * Args:
 *   serverSummary: Diff summary returned from the build snapshot comparison, if available.
 *   localSummary: Section edits saved locally before the server diff observes them.
 *
 * Returns:
 *   A single summary containing both sources, or undefined when neither source exists.
 *
 * Raises:
 *   None.
 */
function mergeDesignChangeSummaries(
  serverSummary: DesignChangeSummary | undefined,
  localSummary: DesignChangeSummary | null,
): DesignChangeSummary | undefined {
  if (!serverSummary) return localSummary ?? undefined;
  if (!localSummary) return serverSummary;

  return {
    sections: {
      added: Array.from(new Set([...serverSummary.sections.added, ...localSummary.sections.added])),
      removed: Array.from(
        new Set([...serverSummary.sections.removed, ...localSummary.sections.removed]),
      ),
      changed: Array.from(
        new Set([...serverSummary.sections.changed, ...localSummary.sections.changed]),
      ),
    },
    screens: {
      added: Array.from(new Set([...serverSummary.screens.added, ...localSummary.screens.added])),
      removed: Array.from(
        new Set([...serverSummary.screens.removed, ...localSummary.screens.removed]),
      ),
      changed: Array.from(
        new Set([...serverSummary.screens.changed, ...localSummary.screens.changed]),
      ),
    },
    graphChanged: serverSummary.graphChanged || localSummary.graphChanged,
  };
}

export function DesignWorkspace({
  gameId,
  buildPlan,
  onSentToBuild,
  onSendUpdate,
  buildActivity,
  autoBuildStatus,
}: DesignWorkspaceProps): ReactElement {
  const [project, setProject] = useState<DesignProject | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<View>({ kind: 'section', filename: '' });
  const [body, setBody] = useState('');
  // Last-built body for the active section — drives item-level "edited since
  // last build" pips. Undefined when there's no build snapshot yet.
  const [baselineBody, setBaselineBody] = useState<string | undefined>(undefined);
  // A pending Co-Designer "apply to section" — reviewed as a diff before it's
  // written, so a partial reply can never silently overwrite the section.
  const [copilotApply, setCopilotApply] = useState<{
    proposed: string;
    syncInstruction?: string;
    /** True when the reply wasn't item-shaped and was captured as a freeform
     *  fallback item — the dialog explains that rather than showing a normal diff. */
    freeform?: boolean;
  } | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [buildOpen, setBuildOpen] = useState(false);
  const [genScreens, setGenScreens] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);
  const [syncDialog, setSyncDialog] = useState<{
    mode: SyncDesignDialogMode;
  } | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [authorshipState, setAuthorshipState] = useState<DesignAuthorshipState>('unknown');
  const [selectedLenses, setSelectedLenses] = useState<LensSelection[]>([]);
  const [screensStale, setScreensStale] = useState(false);
  const [buildStatus, setBuildStatus] = useState<BuildUpdateStatus | null>(null);
  const [localSyncSectionIds, setLocalSyncSectionIds] = useState<string[]>([]);
  const [reconcileStatusState, setReconcileStatusState] = useState<ReconcileStatusState | null>(
    null,
  );
  const [reconcileOpenState, setReconcileOpenState] = useState<ReconcileOpenState>({
    gameId,
    open: false,
  });
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wizardAutoTriggered = useRef(false);
  const saveChain = useRef<Promise<void>>(Promise.resolve());
  const saveVersion = useRef(0);
  const currentGameIdRef = useRef(gameId);
  const buildPlanFocusNonce = buildPlan?.focusNonce ?? 0;
  const buildPlanFocusNonceRef = useRef(buildPlanFocusNonce);
  const autoBuildBusy =
    autoBuildStatus === 'armed' ||
    autoBuildStatus === 'generating-docs' ||
    autoBuildStatus === 'exporting-package' ||
    autoBuildStatus === 'commit-starting';
  currentGameIdRef.current = gameId;
  const reconcileStatus =
    reconcileStatusState?.gameId === gameId ? reconcileStatusState.status : null;
  const reconcileOpen = reconcileOpenState.gameId === gameId ? reconcileOpenState.open : false;

  const setReconcileOpen = useCallback(
    (open: boolean) => {
      setReconcileOpenState({ gameId, open });
    },
    [gameId],
  );

  useEffect(() => {
    buildPlanFocusNonceRef.current = buildPlanFocusNonce;
    if (buildPlanFocusNonce <= 0) return;
    setView({ kind: 'build-plan' });
  }, [buildPlanFocusNonce]);

  // Drives the per-section edited dots, the Sync design gate, and the Build
  // card's primary action. Refreshed on mount/game-switch and explicitly after
  // section saves, screen mutations, syncs, and sends.
  const refreshBuildStatus = useCallback(() => {
    getBuildUpdateStatus(gameId)
      .then(setBuildStatus)
      .catch(() => setBuildStatus(null));
  }, [gameId]);

  const refreshReconcileStatus = useCallback(() => {
    const requestedGameId = gameId;
    getReconcileStatus(requestedGameId)
      .then((status) => {
        if (currentGameIdRef.current !== requestedGameId) return;
        setReconcileStatusState({ gameId: requestedGameId, status });
      })
      .catch(() => {
        if (currentGameIdRef.current !== requestedGameId) return;
        // A failed refresh cannot prove zero unread activity; keep known reviews visible.
        setReconcileStatusState((current) =>
          current?.gameId === requestedGameId && current.status.newMessageCount > 0
            ? current
            : null,
        );
      });
  }, [gameId]);

  // "Generate from design" staleness — per-game, persisted in localStorage.
  useEffect(() => {
    setScreensStale(isScreensStale(gameId));
  }, [gameId]);

  const screensState = useScreens(gameId);
  const activeFilename = view.kind === 'section' && view.filename ? view.filename : null;
  const activeSectionRef = useRef<{ gameId: string; filename: string | null }>({
    gameId,
    filename: activeFilename,
  });

  useEffect(() => {
    activeSectionRef.current = { gameId, filename: activeFilename };
  }, [gameId, activeFilename]);

  const markSectionNeedsSync = useCallback(
    (filename: string) => {
      const sectionId = project?.sections.find((s) => s.filename === filename)?.id;
      if (!sectionId) return;
      setLocalSyncSectionIds((current) =>
        current.includes(sectionId) ? current : [...current, sectionId],
      );
    },
    [project],
  );

  const clearLocalSyncSections = useCallback(() => {
    setLocalSyncSectionIds([]);
  }, []);

  /**
   * Clears local pending sync only for section files reverse reconcile wrote.
   *
   * Args:
   *   filenames: Section filenames reported by the completed reconcile write.
   *
   * Returns:
   *   void.
   *
   * Raises:
   *   None.
   */
  const clearLocalSyncSectionsForFilenames = useCallback(
    (filenames: string[]) => {
      if (filenames.length === 0 || !project) return;

      // Local sync state is section-id based; reconcile reports filenames from disk writes.
      const reconciledIds = new Set(
        project.sections.filter((section) => filenames.includes(section.filename)).map((s) => s.id),
      );
      if (reconciledIds.size === 0) return;

      setLocalSyncSectionIds((current) => current.filter((id) => !reconciledIds.has(id)));
    },
    [project],
  );

  const reloadSectionIfCurrent = useCallback(
    (filename: string) => {
      const requestedGameId = gameId;
      void readSection(requestedGameId, filename)
        .then((text) => {
          const current = activeSectionRef.current;
          if (current.gameId !== requestedGameId || current.filename !== filename) return;
          setLoadError(null);
          setBody(text);
        })
        .catch((err: unknown) => {
          const current = activeSectionRef.current;
          if (current.gameId !== requestedGameId || current.filename !== filename) return;
          setLoadError(err instanceof Error ? err.message : String(err));
        });
    },
    [gameId],
  );

  // Mutation completion can arrive after the dialog closes, so read the live editor ref.
  const reloadActiveSection = useCallback(() => {
    const current = activeSectionRef.current;
    if (current.gameId === gameId && current.filename) reloadSectionIfCurrent(current.filename);
  }, [gameId, reloadSectionIfCurrent]);

  useEffect(() => {
    let cancelled = false;
    wizardAutoTriggered.current = false;
    saveVersion.current += 1;
    setProject(null);
    setLoadError(null);
    setView({ kind: 'section', filename: '' });
    setBody('');
    setLocalSyncSectionIds([]);
    setAuthorshipState('unknown');
    // Reconcile status is per-game; clear it before the next status request resolves.
    setReconcileStatusState(null);
    setReconcileOpenState({ gameId, open: false });
    openProject(gameId)
      .then((p) => {
        if (cancelled) return;
        setProject(p);
        const first = [...p.sections].sort((a, b) => a.order - b.order)[0];
        if (buildPlanFocusNonceRef.current > 0) {
          setView({ kind: 'build-plan' });
        } else if (first) {
          setView({ kind: 'section', filename: first.filename });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [gameId]);

  useEffect(() => {
    if (!activeFilename) return;
    let cancelled = false;
    setSaveState('idle');
    setLoadError(null);
    readSection(gameId, activeFilename)
      .then((text) => {
        if (cancelled) return;
        setLoadError(null);
        setBody(text);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        if (/ENOENT|no such file/i.test(msg)) {
          setBody('');
        } else {
          setLoadError(msg);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, activeFilename]);

  // Fetch the last-built body for the active section so item-mode can show
  // per-item "edited since last build" pips. No snapshot → no baseline (no
  // pips); a section absent from the snapshot resolves '' (all items are new).
  useEffect(() => {
    if (!activeFilename || !buildStatus?.hasSnapshot) {
      setBaselineBody(undefined);
      return;
    }
    let cancelled = false;
    getBuildSnapshotSection(gameId, activeFilename)
      .then((b) => {
        if (!cancelled) setBaselineBody(b ?? '');
      })
      .catch(() => {
        if (!cancelled) setBaselineBody(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, activeFilename, buildStatus?.hasSnapshot, buildStatus?.lastSentAt]);

  // Refresh build/reconcile status on mount/game-switch and running-state flips.
  // Mutating actions refresh explicitly; selecting a different section/screen
  // does not change build state and should not re-read the whole design.
  // While a build runs, poll so the Build card's "N doc edits queued" count
  // and "Review Build activity" trigger stay live.
  const buildRunning = buildPlan?.buildRequestPending ?? false;
  useEffect(() => {
    // Refresh on every running-state flip too, so queued edits surface as
    // pending design changes the moment a build finishes.
    refreshBuildStatus();
    refreshReconcileStatus();
    if (!buildRunning) return;
    const timer = setInterval(() => {
      refreshBuildStatus();
      refreshReconcileStatus();
    }, 15_000);
    return () => clearInterval(timer);
  }, [buildRunning, refreshBuildStatus, refreshReconcileStatus]);

  // Louder nudge: when a build FINISHES (run-state edge into 'done') and the
  // Develop chat made changes not yet recorded in the design, open the review
  // automatically. Still human-gated — nothing writes without confirming the
  // diff. Only the building→done edge fires it, so it won't nag on every visit.
  const prevRunKindRef = useRef(buildActivity?.runState?.kind);
  useEffect(() => {
    const prev = prevRunKindRef.current;
    const cur = buildActivity?.runState?.kind;
    prevRunKindRef.current = cur;
    if (cur !== 'done' || prev === undefined || prev === 'done' || autoBuildBusy) return;
    const requestedGameId = gameId;
    getReconcileStatus(requestedGameId)
      .then((status) => {
        if (currentGameIdRef.current !== requestedGameId) return;
        if (status.newMessageCount > 0) setReconcileOpen(true);
      })
      .catch(() => {
        /* best-effort; the standing "Sync design from chat" badge still shows */
      });
  }, [buildActivity?.runState?.kind, autoBuildBusy, gameId, setReconcileOpen]);

  // Fully empty/scaffolded docs auto-launch the first-run wizard. Any single
  // non-placeholder authored section routes the wand to freeform revision.
  useEffect(() => {
    if (!project) {
      setAuthorshipState('unknown');
      return;
    }
    let cancelled = false;
    Promise.all(project.sections.map((s) => readSection(gameId, s.filename).catch(() => ''))).then(
      (bodies) => {
        if (cancelled) return;
        setAuthorshipState(deriveDesignAuthorship(bodies));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [project, gameId]);

  useEffect(() => {
    if (autoBuildBusy || wizardAutoTriggered.current || authorshipState !== 'scaffolded') return;
    wizardAutoTriggered.current = true;
    setWizardOpen(true);
  }, [authorshipState, autoBuildBusy]);

  const persist = useCallback(
    (filename: string, text: string) => {
      setSaveState('saving');
      const version = saveVersion.current + 1;
      saveVersion.current = version;
      const job = saveChain.current
        .catch(() => {})
        .then(() => writeSection(gameId, filename, text));
      saveChain.current = job.then(
        () => {},
        () => {},
      );
      job
        .then(() => {
          if (version === saveVersion.current) setSaveState('saved');
          if (isAuthoredSectionBody(text)) {
            setAuthorshipState('authored');
          }
          markDocsChanged(gameId);
          markSectionNeedsSync(filename);
          setScreensStale(isScreensStale(gameId));
          refreshBuildStatus();
        })
        .catch(() => {
          if (version === saveVersion.current) setSaveState('error');
        });
    },
    [gameId, markSectionNeedsSync, refreshBuildStatus],
  );

  const onBodyChange = useCallback(
    (text: string) => {
      setBody(text);
      if (!activeFilename) return;
      setSaveState('saving');
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => persist(activeFilename, text), 500);
    },
    [activeFilename, persist],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  if (loadError) {
    return (
      <div className="p-6 text-sm text-red-400" data-testid="design-load-error">
        Failed to load design: {loadError}
      </div>
    );
  }
  if (!project) {
    return (
      <div className="p-6 text-sm text-crucible-muted" data-testid="design-loading">
        Loading design…
      </div>
    );
  }

  const sections = [...project.sections].sort((a, b) => a.order - b.order);
  const statusInputs: DesignStatusInputs = {
    hasPlan: (buildPlan?.plan ?? '').trim().length > 0,
    planRevision: buildPlan?.planRevision,
    planHash: buildPlan?.planHash,
    lastBuiltPlanRevision: buildPlan?.lastBuiltPlanRevision,
    lastBuiltPlanHash: buildPlan?.lastBuiltPlanHash,
    buildRunning: buildPlan?.buildRequestPending ?? false,
    hasInterviewAnswers: (buildPlan?.totalCount ?? buildPlan?.answeredCount ?? 0) > 0,
    autoBuildStatus,
    buildStatus,
    reconcileStatus,
  };
  const designPending = designChangesPending(statusInputs);
  const localSyncSections = localSyncSectionIds
    .map((id) => sections.find((s) => s.id === id))
    .filter((section): section is DesignProject['sections'][number] => section !== undefined);
  const serverChangedSectionIds = buildStatus?.changedSectionIds ?? [];
  const localSyncSectionsOnly = localSyncSections.filter(
    (section) => !serverChangedSectionIds.includes(section.id),
  );
  const localSyncChangedSectionIds = localSyncSectionsOnly.map((s) => s.id);
  const navChangedSectionIds = Array.from(
    new Set([...serverChangedSectionIds, ...localSyncSections.map((s) => s.id)]),
  );
  const localSyncSummary: DesignChangeSummary | null =
    localSyncSectionsOnly.length > 0
      ? {
          sections: {
            added: [],
            removed: [],
            changed: localSyncSectionsOnly.map((s) => s.title),
          },
          screens: { added: [], removed: [], changed: [] },
          graphChanged: false,
        }
      : null;
  const syncBuildStatus: BuildUpdateStatus | null = buildStatus
    ? designPending || localSyncSummary
      ? {
          ...buildStatus,
          hasChanges: buildStatus.hasChanges || localSyncSummary !== null,
          changedCount: buildStatus.changedCount + localSyncChangedSectionIds.length,
          summary: mergeDesignChangeSummaries(buildStatus.summary, localSyncSummary),
          changedSectionIds: navChangedSectionIds,
          changedScreenIds: buildStatus.changedScreenIds ?? [],
        }
      : null
    : localSyncSummary
      ? {
          hasSnapshot: false,
          hasChanges: true,
          changedCount: localSyncChangedSectionIds.length,
          summary: localSyncSummary,
          changedSectionIds: localSyncChangedSectionIds,
          changedScreenIds: [],
        }
      : null;
  const displayStatusInputs: DesignStatusInputs =
    syncBuildStatus?.hasSnapshot === true
      ? { ...statusInputs, buildStatus: syncBuildStatus }
      : statusInputs;
  const syncPending = designPending || syncBuildStatus !== null;
  const syncChangedCount = syncBuildStatus?.changedCount ?? 0;
  const activeSection = activeFilename
    ? (sections.find((s) => s.filename === activeFilename) ?? null)
    : null;
  const updateProfile = (patch: Partial<DesignProfile>): void => {
    setProject((p) => (p ? { ...p, profile: { ...p.profile, ...patch } } : p));
    void writeProjectProfile(gameId, patch)
      .then((updated) => setProject(updated))
      .catch(() => {
        /* best-effort; a reload re-syncs from disk */
      });
  };
  const activeItemMode = activeSection ? itemModeFor(activeSection) : false;
  const toggleSectionMode = (): void => {
    if (!activeSection) return;
    const next = !activeItemMode;
    // Optimistic local update; persist the flag to project.json.
    setProject((p) =>
      p
        ? {
            ...p,
            sections: p.sections.map((s) =>
              s.id === activeSection.id ? { ...s, itemMode: next } : s,
            ),
          }
        : p,
    );
    void setSectionItemMode(gameId, activeSection.id, next).catch(() => {
      // Roll back on failure.
      setProject((p) =>
        p
          ? {
              ...p,
              sections: p.sections.map((s) =>
                s.id === activeSection.id ? { ...s, itemMode: activeItemMode } : s,
              ),
            }
          : p,
      );
    });
  };
  const activeScreen =
    view.kind === 'screen' && view.screenId
      ? (screensState.screens.find((s) => s.id === view.screenId) ?? null)
      : null;
  // Co-Designer apply — never write straight over the section. For an item
  // section we merge the reply's item(s) into the existing ones (by name); for
  // a freeform section we replace. Either way the result is reviewed as a diff
  // (copilotApply → ItemProposalDialog) before anything is written.
  const requestCopilotApply = (markdown: string, syncInstruction?: string): void => {
    if (!activeFilename) return;
    // Item sections merge the reply's item(s) in by name. If the reply isn't
    // item-shaped (the model answered in prose), don't let the merge silently
    // no-op — capture it as a labelled freeform item so it's still reviewable
    // and applyable. Freeform sections replace as before.
    if (activeItemMode && !replyHasItems(markdown)) {
      setCopilotApply({
        proposed: appendFreeformItem(body, markdown, 'Co-Designer notes'),
        syncInstruction,
        freeform: true,
      });
      return;
    }
    const proposed = activeItemMode
      ? mergeItemsMarkdown(body, markdown, {
          pinGameLoop: activeSection ? isGameLoopSection(activeSection.id) : false,
        })
      : markdown;
    setCopilotApply({ proposed, syncInstruction });
  };
  const applyToSection = (markdown: string): void => requestCopilotApply(markdown);

  const newScreen = (): void => {
    void screensState.create().then((s) => {
      setView({ kind: 'screen', screenId: s.id });
      refreshBuildStatus();
    });
  };

  const generateScreens = (): void => {
    setGenScreens(true);
    setGenError(null);
    generateScreensFromDesign({
      gameId,
      project,
      existing: screensState.screens,
      lensIds: selectedLenses,
      save: screensState.save,
    })
      .then((written) => {
        if (written[0]) setView({ kind: 'screen', screenId: written[0].id });
        markScreensGenerated(gameId);
        setScreensStale(isScreensStale(gameId));
        refreshBuildStatus();
      })
      .catch((e: unknown) => setGenError(e instanceof Error ? e.message : String(e)))
      .finally(() => setGenScreens(false));
  };

  return (
    <div className="flex h-full min-h-0" data-testid="design-workspace">
      {/* Grouped left nav — Build / Design Docs / Screens plus a one-line
          pinned footer. All states derive from statusInputs. */}
      <DesignNav
        hasBuildPlan={buildPlan !== undefined}
        buildPlanActive={view.kind === 'build-plan'}
        chip={deriveBuildPlanChip(statusInputs)}
        footer={deriveNavFooterLine(displayStatusInputs)}
        sections={sections}
        activeFilename={activeFilename}
        changedSectionIds={navChangedSectionIds}
        changedScreenIds={buildStatus?.changedScreenIds ?? []}
        screens={screensState.screens}
        activeScreenId={view.kind === 'screen' ? view.screenId : null}
        generate={deriveScreensGenerateState({ stale: screensStale, busy: genScreens })}
        genError={genError}
        profileSlot={<GameProfile profile={project.profile} onChange={updateProfile} />}
        onSelectBuildPlan={() => setView({ kind: 'build-plan' })}
        onSelectSection={(filename) => setView({ kind: 'section', filename })}
        onSelectScreen={(screenId) => setView({ kind: 'screen', screenId })}
        onNewScreen={newScreen}
        onGenerateScreens={generateScreens}
      />

      {/* Centre panel */}
      <div className="flex min-w-0 flex-1">
        {view.kind === 'build-plan' && buildPlan && (
          <PlanWorkspace
            gameId={gameId}
            gameName={buildPlan.gameName}
            plan={buildPlan.plan}
            answeredCount={buildPlan.answeredCount}
            totalCount={buildPlan.totalCount}
            planRevision={buildPlan.planRevision}
            planHash={buildPlan.planHash}
            planUpdatedAt={buildPlan.planUpdatedAt}
            planSource={buildPlan.planSource}
            surface="design"
            showInterviewAnswers={(buildPlan.totalCount ?? buildPlan.answeredCount ?? 0) > 0}
            lastBuiltPlanRevision={buildPlan.lastBuiltPlanRevision}
            lastBuiltPlanHash={buildPlan.lastBuiltPlanHash}
            buildRequestPending={buildPlan.buildRequestPending}
            lastBuiltAt={buildStatus?.lastSentAt}
            busy={buildPlan.busy}
            draftPlanPending={buildPlan.draftPlanPending || buildOpen}
            onSavePlan={buildPlan.onSavePlan}
            onStartBuilding={() => {
              // First builds route through the handoff dialog so the export is
              // reviewed and the snapshot commits — that snapshot is what later
              // lights up Sync design / Send design changes. (Same path the old
              // nav-footer "Send to build" button ran.)
              if (!buildStatus?.hasSnapshot) {
                setBuildOpen(true);
                return;
              }
              void Promise.resolve(buildPlan.onStartBuilding()).catch(() => {});
            }}
            onDraftPlan={() => setBuildOpen(true)}
            onRebuildFromPlan={buildPlan.onRebuildFromPlan}
            onBackToChat={buildPlan.onBackToChat}
            onSubmitRefinement={buildPlan.onSubmitRefinement}
            onStartOver={buildPlan.onStartOver}
          />
        )}

        {view.kind === 'section' && (
          <section className="flex min-w-0 flex-1 flex-col">
            <header className="flex items-center justify-between border-b border-crucible-border px-4 py-2">
              <span className="truncate text-sm text-crucible-muted">
                {activeSection?.title ?? 'No section selected'}
              </span>
              <div className="flex items-center gap-3">
                <span className="text-xs text-crucible-muted" data-testid="design-save-state">
                  {saveState === 'saving' && 'Saving…'}
                  {saveState === 'saved' && 'Saved'}
                  {saveState === 'error' && 'Save failed'}
                </span>
                <Tip
                  align="end"
                  label={
                    autoBuildBusy
                      ? 'Build setup is generating the design docs'
                      : syncPending
                        ? `${syncChangedCount} design change${
                            syncChangedCount === 1 ? '' : 's'
                          } pending — sync to propagate ${
                            syncChangedCount === 1 ? 'it' : 'them'
                          } across the rest of the design`
                        : 'Nothing to sync — edit a design doc and this lights up'
                  }
                >
                  <button
                    type="button"
                    onClick={() => setSyncDialog({ mode: { kind: 'pending-sync' } })}
                    disabled={!syncPending || autoBuildBusy}
                    data-testid="design-sync-button"
                    className={`peer rounded border px-2 py-0.5 text-xs disabled:cursor-not-allowed disabled:opacity-40 ${
                      syncPending
                        ? 'border-crucible-accent/60 text-crucible-accent-hover hover:bg-crucible-bg-elevated'
                        : 'border-crucible-border text-crucible-muted'
                    }`}
                  >
                    Sync design
                  </button>
                </Tip>
                <Tip
                  align="end"
                  label={
                    autoBuildBusy
                      ? 'Build setup is drafting the design docs'
                      : authorshipState === 'authored'
                        ? 'Revise design — propose cross-document edits for review'
                        : 'Design wizard — draft all sections from a pitch interview'
                  }
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (autoBuildBusy || authorshipState === 'unknown') return;
                      if (authorshipState === 'authored') {
                        setSyncDialog({ mode: { kind: 'freeform-revision', source: 'wand' } });
                      } else {
                        setWizardOpen(true);
                      }
                    }}
                    disabled={autoBuildBusy || authorshipState === 'unknown'}
                    data-testid="design-wizard-button"
                    className="peer rounded border border-crucible-border p-1 text-crucible-muted hover:bg-crucible-bg-elevated disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Wand2 size={13} aria-hidden="true" />
                  </button>
                </Tip>
                {activeSection && (
                  <Tip
                    align="end"
                    label={
                      activeItemMode
                        ? 'Switch to document view (one freeform editor)'
                        : 'Switch to items view (discrete cards)'
                    }
                  >
                    <button
                      type="button"
                      onClick={toggleSectionMode}
                      data-testid="design-section-mode-toggle"
                      className="peer rounded border border-crucible-border p-1 text-crucible-muted hover:bg-crucible-bg-elevated"
                    >
                      {activeItemMode ? (
                        <FileText size={13} aria-hidden="true" />
                      ) : (
                        <Rows3 size={13} aria-hidden="true" />
                      )}
                    </button>
                  </Tip>
                )}
                <LensPanel selected={selectedLenses} onChange={setSelectedLenses} />
              </div>
            </header>
            <div className="flex min-h-0 flex-1">
              {activeSection ? (
                activeItemMode ? (
                  <SectionItemsEditor
                    value={body}
                    onChange={onBodyChange}
                    gameId={gameId}
                    sectionTitle={activeSection.title}
                    projectName={project?.name ?? null}
                    baselineBody={baselineBody}
                    hostsGameLoop={isGameLoopSection(activeSection.id)}
                  />
                ) : (
                  <SectionWysiwyg value={body} onChange={onBodyChange} />
                )
              ) : (
                <div className="p-4 text-sm text-crucible-muted">Select a section.</div>
              )}
            </div>
          </section>
        )}

        {view.kind === 'screen' &&
          (activeScreen ? (
            <ScreenDetail
              key={activeScreen.id}
              screen={activeScreen}
              onChange={(s) => void screensState.save(s).then(refreshBuildStatus)}
              onDelete={() => {
                const id = activeScreen.id;
                void screensState
                  .remove(id)
                  .then(() => setView({ kind: 'screen', screenId: null }))
                  .then(refreshBuildStatus);
              }}
            />
          ) : (
            <div className="flex-1 p-4 text-sm text-crucible-muted">Select or create a screen.</div>
          ))}
      </div>

      <SyncDesignDialog
        gameId={gameId}
        project={project}
        open={syncDialog !== null}
        mode={syncDialog?.mode ?? { kind: 'pending-sync' }}
        buildStatus={syncBuildStatus}
        onOpenChange={(next) => {
          if (!next) setSyncDialog(null);
        }}
        lensIds={selectedLenses}
        onApplied={() => {
          clearLocalSyncSections();
          if (activeFilename) reloadSectionIfCurrent(activeFilename);
          refreshBuildStatus();
        }}
      />

      {copilotApply && (
        <ItemProposalDialog
          open
          title={`Apply to ${activeSection?.title ?? 'section'}`}
          applyLabel={copilotApply.freeform ? 'Append to section' : 'Apply to section'}
          note={
            copilotApply.freeform
              ? "The Co-Designer replied in prose, not editable items — it's captured below as a new note you can reshape into items."
              : undefined
          }
          original={body}
          proposed={copilotApply.proposed}
          onApply={(text) => {
            onBodyChange(text);
            if (copilotApply.syncInstruction) {
              setSyncDialog({
                mode: {
                  kind: 'freeform-revision',
                  initialInstruction: copilotApply.syncInstruction,
                },
              });
            }
          }}
          onOpenChange={(o) => {
            if (!o) setCopilotApply(null);
          }}
        />
      )}

      <WizardDialog
        gameId={gameId}
        project={project}
        open={wizardOpen}
        onOpenChange={(next) => {
          if (next && autoBuildBusy) return;
          setWizardOpen(next);
        }}
        lensIds={selectedLenses}
        hasExistingContent={authorshipState === 'authored'}
        onApplied={() => {
          setAuthorshipState('authored');
          if (activeFilename) reloadSectionIfCurrent(activeFilename);
          refreshBuildStatus();
        }}
      />

      <BuildHandoffDialog
        gameId={gameId}
        open={buildOpen}
        onOpenChange={setBuildOpen}
        onSent={() => {
          clearLocalSyncSections();
          refreshBuildStatus();
        }}
        onStartBuild={(seededPlan) => onSentToBuild?.(seededPlan)}
        onSendUpdate={(message) => onSendUpdate?.(message)}
      />

      <ReconcileDialog
        gameId={gameId}
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        isItemSection={(id) => {
          const s = project.sections.find((x) => x.id === id);
          return s ? itemModeFor(s) : itemModeFor({ id });
        }}
        onMutationComplete={({ applied, filenames }) => {
          if (applied) reloadActiveSection();
          if (applied) clearLocalSyncSectionsForFilenames(filenames);
          refreshBuildStatus();
          refreshReconcileStatus();
        }}
      />

      {/* Right rail — compact Build card stacked over the Co-Designer. The
          card owns build status AND its action. */}
      <aside className="flex w-80 shrink-0 flex-col border-l border-crucible-border">
        <BuildRailCard
          model={deriveBuildCardModel(displayStatusInputs)}
          buildActivity={buildActivity}
          onPrimaryAction={(action) => {
            if (action.kind === 'review-build-changes') {
              setReconcileOpen(true);
              return;
            }
            setBuildOpen(true);
          }}
        />
        <CoPilotPanel
          gameId={gameId}
          projectName={project.name}
          projectType={project.projectType}
          sectionTitle={view.kind === 'section' ? (activeSection?.title ?? null) : null}
          sectionDoc={view.kind === 'section' ? (activeSection?.doc ?? null) : null}
          sectionBody={view.kind === 'section' ? body : ''}
          sectionIsItems={view.kind === 'section' ? activeItemMode : false}
          project={project}
          lensIds={selectedLenses}
          onApplyToSection={view.kind === 'section' && activeFilename ? applyToSection : undefined}
          onApplyAndSync={
            view.kind === 'section' && activeFilename
              ? (markdown) => requestCopilotApply(markdown, markdown)
              : undefined
          }
          onAdoptRecommendation={(instruction) =>
            setSyncDialog({
              mode: {
                kind: 'freeform-revision',
                initialInstruction: instruction,
                source: 'review-recommendation',
              },
            })
          }
          className="min-h-0 flex-1"
        />
      </aside>
    </div>
  );
}
